import { useEffect, useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { triggerAnalyticsForRenotifyFetchInactiveUsersFailed, triggerAnalyticsForRenotifyFetchInactiveUsersSucceeded, triggerAnalyticsForRenotifyNotEligible } from '../components/analytics';
import { usePendingUsersService } from './usePendingUsersService';
export const mapDataCache = new Map();
export const PENDING_USERS_CACHE_KEY = 'pendingUsers';
export const RENOTIFY_ELIGIBILITY_CACHE_KEY = 'renotifyEligibility';
export let RenotifyStatus = /*#__PURE__*/function (RenotifyStatus) {
  RenotifyStatus["IS_LOADING"] = "isLoading";
  RenotifyStatus["ELIGIBLE"] = "eligible";
  RenotifyStatus["INELIGIBLE"] = "ineligible";
  RenotifyStatus["ERROR"] = "error";
  return RenotifyStatus;
}({});
const REQUEST_CACHE_TIMEOUT_PENDING_USERS = 10 * 60 * 1000; // 10 minutes

export function usePendingUsers({
  cloudId,
  startFetching,
  renotifyExperiment,
  product,
  requestCacheTimeout
}) {
  const {
    createAnalyticsEvent
  } = useAnalyticsEvents();
  const [pendingUsers, setPendingUsers] = useState(mapDataCache.get(PENDING_USERS_CACHE_KEY));
  const [renotifyEligible, setRenotifyEligibility] = useState(mapDataCache.get(RENOTIFY_ELIGIBILITY_CACHE_KEY));
  const {
    loading: pendingUsersLoading,
    error: pendingUsersError,
    data: pendingUsersData,
    fetchData: fetchPendingUsers
  } = usePendingUsersService(cloudId, requestCacheTimeout === undefined ? REQUEST_CACHE_TIMEOUT_PENDING_USERS : requestCacheTimeout, product);
  useEffect(() => {
    if (renotifyEligible !== undefined || renotifyExperiment !== null && renotifyExperiment !== void 0 && renotifyExperiment.isLoading || !startFetching) {
      return;
    }
    if (renotifyExperiment !== null && renotifyExperiment !== void 0 && renotifyExperiment.isEnrolled || renotifyExperiment !== null && renotifyExperiment !== void 0 && renotifyExperiment.isJswTargeted) {
      fetchPendingUsers();
    } else {
      mapDataCache.set(RENOTIFY_ELIGIBILITY_CACHE_KEY, false);
      setRenotifyEligibility(false);
    }
  }, [renotifyEligible, fetchPendingUsers, renotifyExperiment === null || renotifyExperiment === void 0 ? void 0 : renotifyExperiment.isLoading, renotifyExperiment === null || renotifyExperiment === void 0 ? void 0 : renotifyExperiment.isEnrolled, renotifyExperiment === null || renotifyExperiment === void 0 ? void 0 : renotifyExperiment.isJswTargeted, startFetching]);
  useEffect(() => {
    if (pendingUsersError) {
      triggerAnalyticsForRenotifyFetchInactiveUsersFailed(createAnalyticsEvent);
      mapDataCache.set(RENOTIFY_ELIGIBILITY_CACHE_KEY, false);
      setRenotifyEligibility(false);
      return;
    }
    if (pendingUsersData === undefined || pendingUsersLoading) {
      return;
    }
    mapDataCache.set(PENDING_USERS_CACHE_KEY, pendingUsersData);
    setPendingUsers(pendingUsersData);
    if (pendingUsersData !== null && pendingUsersData !== void 0 && pendingUsersData.length) {
      var _renotifyExperiment$o;
      triggerAnalyticsForRenotifyFetchInactiveUsersSucceeded(createAnalyticsEvent, {
        numberOfInviteeEmails: pendingUsersData === null || pendingUsersData === void 0 ? void 0 : pendingUsersData.length
      });
      mapDataCache.set(RENOTIFY_ELIGIBILITY_CACHE_KEY, true);
      // fire jsw renotify exposure but not impact confluence side
      if (renotifyExperiment !== null && renotifyExperiment !== void 0 && renotifyExperiment.isEnrolled || (renotifyExperiment === null || renotifyExperiment === void 0 ? void 0 : (_renotifyExperiment$o = renotifyExperiment.onFetchedPendingUsers) === null || _renotifyExperiment$o === void 0 ? void 0 : _renotifyExperiment$o.call(renotifyExperiment)) === true) {
        setRenotifyEligibility(true);
      }
    } else {
      triggerAnalyticsForRenotifyNotEligible(createAnalyticsEvent, {
        reason: 'No pending users'
      });
      mapDataCache.set(RENOTIFY_ELIGIBILITY_CACHE_KEY, false);
      setRenotifyEligibility(false);
    }
  }, [createAnalyticsEvent, pendingUsersData, pendingUsersError, pendingUsersLoading, renotifyExperiment]);
  if (pendingUsersLoading || renotifyExperiment !== null && renotifyExperiment !== void 0 && renotifyExperiment.isLoading) {
    return {
      status: RenotifyStatus.IS_LOADING,
      isLoading: true
    };
  }
  if (pendingUsersError) {
    return {
      status: RenotifyStatus.ERROR,
      pendingUsersError: pendingUsersError !== null && pendingUsersError !== void 0 ? pendingUsersError : new Error('Invalid pending user response'),
      isLoading: false
    };
  }
  if (renotifyEligible === false || !pendingUsers) {
    return {
      status: RenotifyStatus.INELIGIBLE,
      isLoading: false
    };
  }
  return {
    status: RenotifyStatus.ELIGIBLE,
    isLoading: false,
    pendingUsers
  };
}